import type {
    ChangeEventHandler, DOMAttributes} from 'react';
import {
    useCallback,
    useEffect,
    useMemo,
    useRef
} from 'react'
import assert from 'assert'
import {useCallbackRef} from '../../hooks/useCallbackRef'
import {isPlainObject} from '../../util/is-type'

type HtmlPropsWithoutDomAttributes<T> = Omit<React.OptionHTMLAttributes<T>, keyof DOMAttributes<T>>

type SelectOption<T> = Override<HtmlPropsWithoutDomAttributes<HTMLOptionElement>, {
    value: T
    text?: string
    key?: React.Key
}, 'selected'|'defaultSelected'|'defaultChecked'|'defaultValue'>

export interface SelectChangeEvent<T> {
    // option: SelectOption<T>,
    value: T
    target: HTMLSelectElement
    selectedIndex: number
}

export type SelectChangeEventHandler<T> = (ev: SelectChangeEvent<T>) => void

type SelectProps<T> = Override<React.ComponentPropsWithoutRef<'select'>, {
    options: SelectOption<T>[]|T[]
    onChange?: SelectChangeEventHandler<T>
    value?: T
}, 'multiple'>

function setRef<T>(ref: React.ForwardedRef<T>, value: T|null) {
    if(ref) {
        if(typeof ref === 'function') {
            ref(value)
        } else {
            ref.current = value
        }
    }
}

function useMutableRef<T>(){
    return useRef<T|null>(null);
}

function useForwardedRef<T>(forwardedRef: React.ForwardedRef<T>, callback: (current:T)=>void): [callback: React.RefCallback<T>, current: T|null] {
    const refObj = useMutableRef<T>()
    const cbRef = useCallbackRef(callback)

    const cb = useCallback((current: T) => {
        setRef(forwardedRef, refObj.current = current)
        if(current !== null) {
            cbRef.current(current)
        }
    }, [forwardedRef, refObj, cbRef])

    return [cb, refObj.current]
}

function fixOption<T>(o: Partial<SelectOption<T>>|T): SelectOption<T> {
    if(isPlainObject(o)) {
        if(!Object.hasOwnProperty.call(o, 'value')) {
            if(!Object.hasOwnProperty.call(o, 'text')) {
                return {...o, value: null, text: ""}
            }
            return {...o, value: o.text}
        }
        if(!Object.hasOwnProperty.call(o, 'text')) {
            return {...o, text: String(o.value)}
        }
        return o;
    }
    return {text: String(o), value: o}
}


// FIXME: https://stackoverflow.com/questions/71476865/how-to-make-higher-order-component-generic
// type SelectComponent<T> = ForwardRefExoticComponent<PropsWithoutRef<SelectProps<T>> & RefAttributes<HTMLSelectElement>>

function Select<T>({onChange,options: _options, value,...props}: SelectProps<T>, forwardedRef: React.ForwardedRef<HTMLSelectElement>)  {
    const options: SelectOption<T>[] = useMemo(() => _options?.length ? _options.map(fixOption) : [], [_options])
    const onChangeRef = useCallbackRef(onChange)

    const changeHandler = useCallback<ChangeEventHandler<HTMLSelectElement>>(ev => {
        const idx = ev.target.selectedIndex
        assert(options[idx])
        onChangeRef.current?.({
            // option,
            value: options[idx].value,
            selectedIndex: idx,
            target: ev.target,
        })
    }, [onChangeRef, options])


    const [selectRef, select] = useForwardedRef(forwardedRef, select => {
        select.selectedIndex = options.findIndex(opt => opt.value === value)
    })

    useEffect(() => {
        if(!select) return
        const option = select.selectedIndex >= 0 && select.selectedIndex < options.length ? options[select.selectedIndex] : null
        if(!option || option.value !== value) {
            select.selectedIndex = options.findIndex(opt => opt.value === value)
        }
    }, [options, select, value])

    return (
        <select {...props} onChange={changeHandler} ref={selectRef}>
            {options?.map(({value,text,key,...other}, idx) => <option key={key ?? idx} {...other}>{text ?? value}</option>)}
        </select>
    )
}

// const Select = forwardRef<HTMLSelectElement, SelectProps>()
// Select.displayName = "Select"

export default React.forwardRef(Select) as typeof Select  // FIXME: still not the right type; should be ForwardRefExoticComponent<PropsWithoutRef<SelectProps<T>> & RefAttributes<HTMLSelectElement>>
